import {
  ErrorModal,
  getMomentTz,
  HelmetDexteel,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { useEffect, useState } from "react";

import { GridApi } from "ag-grid-community";
import { Moment } from "moment";
import { Grid, Paper, Typography } from "@mui/material";
import { NotificationSnackBar } from "../../controls/snackbars/notification-snackbar";
import { exportToExcel } from "../../utils/excelExportOptions";
import { BilletsYardTable } from "./components/BilletsYardTable";
import { Filters } from "./components/filters/Filters";
import { HeatFilterOption } from "./components/filters/HeatFilter";
import { TransactionTypeOption } from "./components/filters/TransactionTypeFilter";
import { BilletHistory } from "./models/BilletsHistory";
import { getBilletsHistory } from "./repositories/BilletsYardRepository";
import { ExcelButton } from "../../controls/buttons/ExcelButton";
import { useResize } from "../../utils/useResize";
import { isMobileDevice } from "../../utils/responsive-utils";

const moment = getMomentTz();

export const BilletsYardPage = () => {
  const [billets, setBillets] = useState<BilletHistory[]>([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [notificationMessage, setNotificationMessage] = useState("");
  const { width } = useResize();
  const isMobile = isMobileDevice(width);
  const { state: utlSettings } = useUTLSettingsContext();
  const plantZoneReady = Boolean(utlSettings?.settings?.TimeZone);

  const [operatorId, setOperatorId] = useState<number | null>(null);
  const [operatorName, setOperatorName] = useState("All");
  const [locationName, setLocationName] = useState("All");
  const [heatId, setHeatId] = useState<number | null>(null);
  const [operationTypeCode, setOperationTypeCode] = useState<string | null>(
    null,
  );
  const [locationId, setLocationId] = useState<number | null>(null);
  const [heatOptions, setHeatOptions] = useState<HeatFilterOption[]>([]);
  const [typeOptions, setTypeOptions] = useState<TransactionTypeOption[]>([]);
  const [filterStartDate, setFilterStartDate] = useState<Moment>(
    moment().subtract(7, "days").startOf("day"),
  );
  const [filterEndDate, setFilterEndDate] = useState<Moment>(
    moment().endOf("day"),
  );

  const getDataFromAPI = async () => {
    if (!filterStartDate || !filterEndDate) {
      setError("Please select both start and end dates.");
      return;
    }

    setIsLoading(true);
    const resp = await getBilletsHistory({
      filterStartDate,
      filterEndDate,
      operatorId,
      heatId,
      operationTypeCode,
      locationId,
    });
    if (resp.ok) {
      setBillets(resp.data!);
    } else {
      setError(resp.message!);
    }
    setIsLoading(false);
  };

  const handleFilterChange = (startDate: Moment, endDate: Moment) => {
    setFilterStartDate(startDate);
    setFilterEndDate(endDate);
  };

  const handleOperatorChange = (id: number | null, name?: string) => {
    setOperatorId(id);
    setOperatorName(name ?? "All");
  };

  const handleLocationChange = (id: number | null, name?: string) => {
    setLocationId(id);
    setLocationName(name ?? "All");
  };

  useEffect(() => {
    if (heatId === null) {
      const heats = new Map<number, string>();
      for (const billet of billets) {
        if (billet.HeatID && billet.HeatNumber) {
          heats.set(billet.HeatID, billet.HeatNumber);
        }
      }
      setHeatOptions(
        Array.from(heats, ([HeatId, HeatName]) => ({ HeatId, HeatName })).sort(
          (a, b) => b.HeatName.localeCompare(a.HeatName),
        ),
      );
    }
  }, [billets, heatId]);

  useEffect(() => {
    if (operationTypeCode === null) {
      const types = new Map<string, string>();
      for (const billet of billets) {
        if (billet.OperationTypeCode && billet.TransactionType) {
          types.set(billet.OperationTypeCode, billet.TransactionType);
        }
      }
      setTypeOptions(
        Array.from(types, ([Code, Description]) => ({
          Code,
          Description,
        })).sort((a, b) => a.Description.localeCompare(b.Description)),
      );
    }
  }, [billets, operationTypeCode]);

  const handleExportToExcel = () => {
    if (gridApi) {
      setIsExporting(true);
      try {
        const heatName =
          heatOptions.find((h) => h.HeatId === heatId)?.HeatName ?? "All";
        const typeName =
          typeOptions.find((t) => t.Code === operationTypeCode)?.Description ??
          "All";

        exportToExcel({
          gridApi: gridApi,
          fileName: "BilletsYardHistory",
          sheetName: "Billets Yard History",
          startDate: filterStartDate.toDate(),
          endDate: filterEndDate.toDate(),
          userName: {
            label: "Operator",
            value: operatorName,
          },
          additionalPrependContent: [
            { label: "Heat", value: heatName },
            { label: "Type", value: typeName },
            { label: "Location", value: locationName },
          ],
          additionalAppendContent: [
            {
              label: "Total Records:",
              value: (billets?.length || 0).toString(),
            },
          ],
        });
      } catch (error) {
        setError(
          "An error occurred while exporting to Excel. Please try again.",
        );
      } finally {
        setIsExporting(false);
      }
    } else {
      setError("Unable to export data. Please try again.");
    }
  };

  useEffect(() => {
    if (!plantZoneReady) return;
    getDataFromAPI();
  }, [
    plantZoneReady,
    filterStartDate,
    filterEndDate,
    operatorId,
    heatId,
    operationTypeCode,
    locationId,
  ]);

  return (
    <>
      <HelmetDexteel title={`Billets Yard History`} />
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Billets Yard History
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item container xs={12} md={10} spacing={2}>
                <Filters
                  onFilterChange={handleFilterChange}
                  initialStartDate={filterStartDate}
                  initialEndDate={filterEndDate}
                  initialOperator={operatorId}
                  onChangeOperator={handleOperatorChange}
                  heatOptions={heatOptions}
                  initialHeat={heatId}
                  onChangeHeat={setHeatId}
                  typeOptions={typeOptions}
                  initialOperationType={operationTypeCode}
                  onChangeOperationType={setOperationTypeCode}
                  initialLocation={locationId}
                  onChangeLocation={handleLocationChange}
                />
              </Grid>
              {!isMobile && (
                <Grid item xs={12} md={2} container justifyContent="flex-end">
                  <ExcelButton
                    handleExportToExcel={handleExportToExcel}
                    disabled={!billets || billets.length === 0 || isExporting}
                    label={isExporting ? "EXPORTING..." : "EXPORT TO EXCEL"}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <BilletsYardTable
            isLoading={isLoading}
            rows={billets || []}
            refreshData={() => getDataFromAPI()}
            setGridApi={setGridApi}
          />
        </Grid>
      </Grid>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
